import * as React from 'react';
import {
  HexColorPicker,
  RgbColorPicker,
  RgbaColorPicker,
  HslColorPicker,
  HsvColorPicker,
  HexColorInput,
} from 'react-colorful';
import {Popover, PopoverContent, PopoverTrigger} from '@/shadcn_components/ui/popover';
import {Button} from '@/shadcn_components/ui/button';
import {cn} from '@/lib/tailwind-utils';

type ColorPickerType = 'hex' | 'rgb' | 'rgba' | 'hsl' | 'hsv';

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  className?: string;
  type?: ColorPickerType;
  presetColors?: string[];
  showPreset?: boolean;
  showInput?: boolean;
  showHexPicker?: boolean;
}

const PRESET_COLORS = [
  '#ff0000',
  '#ff4d00',
  '#ff9900',
  '#ffff00',
  '#99ff00',
  '#00ff00',
  '#00ff99',
  '#00ffff',
  '#0099ff',
  '#0000ff',
  '#9900ff',
  '#ff00ff',
  '#ff0099',
  '#ffffff',
  '#000000',
];

export const ColorPickerPopup = React.forwardRef<HTMLDivElement, ColorPickerProps>(
  (props, ref) => {
    const {color, onChange, className, presetColors = PRESET_COLORS} = props;
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn('w-[60px] h-[30px] p-0 flex items-center justify-center', className)}
            style={{backgroundColor: color}}>
            <span className="sr-only">Pick a color</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3">
          <ColorPicker color={color} onChange={onChange} presetColors={presetColors} />
        </PopoverContent>
      </Popover>
    );
  },
);

export const ColorPicker = React.forwardRef<HTMLDivElement, ColorPickerProps>(
  (props, ref) => {
    const {color, onChange, presetColors = PRESET_COLORS} = props;
    const {showInput = true, showHexPicker = true} = props;
    return (
      <div className='space-y-2'>
        <div className="flex flex-row flex-wrap">
          {presetColors.map(presetColor => (
            <button
              key={presetColor}
              className="w-6 h-6 border border-input cursor-pointer p-0 m-0 hover:-translate-y-1 hover:ring-primary"
              style={{backgroundColor: presetColor}}
              onClick={() => onChange(presetColor)}
              title={presetColor}
            />
          ))}
        </div>
        {showHexPicker && <HexColorPicker color={color} onChange={onChange} />}
        {showInput && <HexColorInput
          color={color}
          onChange={onChange}
          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:bg-secondary disabled:text-muted-foreground"
        />}
      </div>
    );
  },
);

ColorPickerPopup.displayName = 'ColorPickerPopup';
ColorPicker.displayName = 'ColorPicker';
